// src/pages/fleet/master/VehicleMasterPage.tsx — Vehicle master on LIVE data.
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import MasterTable, { Pill, FormValues, FieldValue } from './MasterTable';
import type { Vehicle } from '../../../types';
import { getVehicles, addVehicle, updateVehicle } from '../../../services/fleetApi';
import { providerName } from '../gpsProvider';

type VehicleRow = Vehicle & { active: boolean };

const STATUS: Record<string, { label: string; color: string; bg: string }> = {
  IDLE:    { label: 'Idle',    color: 'var(--fx-text-2)', bg: 'var(--fx-gray-soft)' },
  ON_TRIP: { label: 'On Trip', color: 'var(--fx-green)',  bg: 'var(--fx-green-soft)' },
  OFFLINE: { label: 'Offline', color: 'var(--fx-red)',    bg: 'var(--fx-red-soft)' },
};
const TYPES = ['TRUCK', 'TRAILER', 'TEMPO', 'PICKUP'];
// The only telematics providers RFCL uses.
// The value is what the column stores and what every poller matches on; the label
// is how the company writes its own name. Splitting them keeps 'Margogps' out of
// the screens without touching a single row.
const GPS_PROVIDER_VALUES = ['Wheelseye', 'Blackbuck', 'Margogps'];
const GPS_PROVIDERS = GPS_PROVIDER_VALUES.map((value) => ({ value, label: providerName(value) as string }));

function ago(d: string | null | undefined): string {
  if (!d) return '—';
  const m = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} hr ago`;
  return `${Math.round(m / 1440)} day ago`;
}

export default function VehicleMasterPage() {
  const [params] = useSearchParams();   // ?status=IDLE from the dashboard cards
  const [rows, setRows] = useState<VehicleRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => getVehicles()
    .then((r) => setRows((r.data.vehicles || []).map((v) => ({ ...v, active: v.isActive !== false }))))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const clean = (v: string | null | undefined) => (v && v !== '—' ? v : '');

  const onSave = async (form: FormValues, existing: VehicleRow | null) => {
    const str = (v: FieldValue) => String(v ?? '').trim();
    // Driver name / mobile are not asked here — they belong to the trip, and the
    // telematics feed fills them in when the provider knows them.
    const payload = {
      vehicleNumber: str(form.vehicleNumber), vehicleType: str(form.vehicleType),
      gpsDeviceId: str(form.gpsDeviceId), gpsProvider: str(form.gpsProvider), isActive: form.active !== false,
      ifmsWhitelisted: form.ifmsWhitelisted === true,
    };
    if (existing) await updateVehicle(existing.id, payload);
    else await addVehicle(payload);
    await load();
  };
  const onToggle = async (r: VehicleRow) => { await updateVehicle(r.id, { isActive: !r.active }); load(); };

  return (
    <MasterTable<VehicleRow>
      icon="truck" title="Vehicle"
      subtitle={loading ? 'Loading…' : `${rows.length} vehicles registered`}
      rows={rows} addLabel="Vehicle"
      initialFilters={params.get('status') ? { status: params.get('status') as string } : undefined}
      searchKeys={['vehicleNumber', 'gpsDeviceId', 'deviceImei', 'vendorName', 'gpsProvider']}
      filters={[
        { key: 'status', label: 'Trip status', options: Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label })) },
        { key: 'vehicleType', label: 'Type', options: TYPES },
        { key: 'ifmsWhitelisted', label: 'IFMS', options: [{ value: 'true', label: 'Whitelisted' }, { value: 'false', label: 'Not listed' }] },
        { key: 'gpsProvider', label: 'GPS', options: GPS_PROVIDERS },
      ]}
      stats={[
        { label: 'Total Vehicles', value: rows.length, tint: 'var(--fx-accent)' },
        { label: 'On Trip', value: rows.filter((r) => r.status === 'ON_TRIP').length, tint: 'var(--fx-green)' },
        { label: 'Idle', value: rows.filter((r) => r.status === 'IDLE').length, tint: 'var(--fx-text-3)' },
        { label: 'IFMS whitelisted', value: rows.filter((r) => r.ifmsWhitelisted).length, tint: 'var(--fx-green)' },
      ]}
      columns={[
        { key: 'vehicleNumber', label: 'Vehicle No.', render: (r) => <b>{r.vehicleNumber}</b> },
        { key: 'ifmsWhitelisted', label: 'IFMS', csvValue: (r) => (r.ifmsWhitelisted ? 'Whitelisted' : 'Not whitelisted'),
          render: (r) => (r.ifmsWhitelisted
            ? <span title={r.whitelistNote || ''} style={{ fontSize: 11, fontWeight: 600, color: 'var(--fx-green)', background: 'var(--fx-green-soft)', padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>Whitelisted</span>
            : <span title="IFMS will reject trips on this truck until VLTS whitelists it" style={{ fontSize: 11, fontWeight: 600, color: 'var(--fx-orange)', background: 'var(--fx-orange-soft)', padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>Not listed</span>) },
        // The CSV keeps the stored spelling, so a file exported here can be read back
        // by the importers without relying on them to fold case.
        { key: 'gpsProvider', label: 'GPS Provider', render: (r) => (r.gpsProvider
          ? <span style={{ whiteSpace: 'nowrap' }}>{providerName(r.gpsProvider)}</span>
          : <span style={{ color: 'var(--fx-text-3)' }}>—</span>), csvValue: (r) => r.gpsProvider || '' },
        { key: 'gpsDeviceId', label: 'GPS Device', render: (r) => (r.gpsDeviceId
          ? <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{r.gpsDeviceId}</span>
          : <span style={{ color: 'var(--fx-orange)', fontWeight: 600 }}>Not linked</span>) },
        { key: 'vendorName', label: 'Transporter', render: (r) => (r.vendorName
          ? <span title={r.vendorCode || ''}>{r.vendorName}</span>
          : <span style={{ color: 'var(--fx-text-3)' }}>—</span>), csvValue: (r) => r.vendorName || '' },
        { key: 'vehicleType', label: 'Type' },
        { key: 'status', label: 'Trip Status', render: (r) => {
          const s = STATUS[r.status] || { label: r.status, color: 'var(--fx-text-2)', bg: 'var(--fx-gray-soft)' };
          return <span style={{ fontSize: 11, fontWeight: 600, color: s.color, background: s.bg, padding: '2px 9px', borderRadius: 20 }}>{s.label}</span>;
        } },
        { key: 'lastPingAt', label: 'Last GPS', render: (r) => ago(r.lastGpsAt || r.lastPingAt), csvValue: (r) => r.lastGpsAt || r.lastPingAt || '' },
        { key: 'active', label: 'Status', render: (r) => <Pill on={r.active} />, csvValue: (r) => (r.active ? 'Active' : 'Inactive') },
      ]}
      fields={[
        { key: 'vehicleNumber', label: 'Vehicle Number', required: true, placeholder: 'e.g. AP16W1415' },
        { key: 'vehicleType', label: 'Vehicle Type', type: 'select', required: true, options: TYPES, default: 'TRUCK' },
        { key: 'gpsProvider', label: 'GPS Provider', type: 'select', required: true, options: GPS_PROVIDERS, default: 'Wheelseye' },
        { key: 'gpsDeviceId', label: 'GPS Device ID', placeholder: 'IMEI or provider device id' },
        { key: 'ifmsWhitelisted', label: 'IFMS whitelisted', type: 'toggle', default: false,
          onLabel: 'Whitelisted — trips allowed', offLabel: 'Not on the VLTS list',
          hint: 'On only if VLTS has whitelisted this truck — trips can be created on whitelisted trucks alone.' },
        { key: 'active', label: 'Status', type: 'toggle', default: true },
      ]}
      onSave={onSave} onToggle={onToggle}
    />
  );
}
